import { useCallback, useEffect, useRef, useState } from 'react'

import MessagesApiService from '@/apis/messagesApiService'

export type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused'

interface UseAudioPlaybackReturn {
  playbackState: PlaybackState
  activeMessageId: string | null
  toggle: (messageId: string) => void
}

export default function useAudioPlayback(): UseAudioPlaybackReturn {
  const [playbackState, setPlaybackState] = useState<PlaybackState>('idle')
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const cacheRef = useRef<Map<string, string>>(new Map())

  const stopCurrent = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.onended = null
      audioRef.current = null
    }
  }, [])

  const toggle = useCallback(
    async (messageId: string) => {
      // Same message playing → pause
      if (activeMessageId === messageId && playbackState === 'playing') {
        audioRef.current?.pause()
        setPlaybackState('paused')
        return
      }

      // Same message paused → resume
      if (activeMessageId === messageId && playbackState === 'paused') {
        await audioRef.current?.play()
        setPlaybackState('playing')
        return
      }

      // Different or new message
      stopCurrent()
      setActiveMessageId(messageId)
      setPlaybackState('loading')

      try {
        let blobUrl = cacheRef.current.get(messageId)
        if (!blobUrl) {
          const blob = await MessagesApiService.audio(messageId)
          blobUrl = URL.createObjectURL(blob)
          cacheRef.current.set(messageId, blobUrl)
        }

        const audio = new Audio(blobUrl)
        audioRef.current = audio

        audio.onended = () => {
          setPlaybackState('idle')
          setActiveMessageId(null)
          audioRef.current = null
        }

        await audio.play()
        setPlaybackState('playing')
      } catch {
        setPlaybackState('idle')
        setActiveMessageId(null)
      }
    },
    [activeMessageId, playbackState, stopCurrent],
  )

  useEffect(() => {
    const cache = cacheRef.current
    return () => {
      stopCurrent()
      cache.forEach((url) => URL.revokeObjectURL(url))
      cache.clear()
    }
  }, [stopCurrent])

  return { playbackState, activeMessageId, toggle }
}
